import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ClipboardCheck, ArrowRight } from "lucide-react";

const STATUS_COLORS = {
  "Ready for Review": "bg-blue-500/10 text-blue-400 border-blue-500/20",
  "Under Review": "bg-amber-500/10 text-amber-400 border-amber-500/20",
  "Revision Required": "bg-orange-500/10 text-orange-400 border-orange-500/20",
};

export default function Approvals() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/approvals")
      .then((r) => setProjects(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="approvals-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">Approvals Queue</h1>
        <p className="text-sm text-slate-500 mt-1">Projects requiring review and approval</p>
      </div>

      <Card className="bg-slate-900 border-slate-800 overflow-hidden">
        <CardHeader className="border-b border-slate-800 py-3 px-4">
          <CardTitle className="font-heading text-base font-semibold text-slate-100 flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-blue-500" strokeWidth={1.5} />
            {projects.length} project{projects.length !== 1 ? "s" : ""} pending
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 px-4">Project</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 px-4">Client</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 px-4">Owner</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 px-4">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 px-4">Updated</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 px-4 w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={`skeleton-${i}`} className="border-slate-800"><TableCell colSpan={6} className="px-4 py-3"><div className="h-4 bg-slate-800 rounded animate-pulse" /></TableCell></TableRow>
                ))
              ) : projects.length > 0 ? (
                projects.map((p) => (
                  <TableRow key={p.id} className="border-slate-800 hover:bg-slate-800/50" data-testid={`approval-row-${p.id}`}>
                    <TableCell className="px-4 py-3">
                      <Link to={`/projects/${p.id}`} className="text-sm font-medium text-slate-200 hover:text-blue-400 transition-colors">{p.name}</Link>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-sm text-slate-400">{p.client_name}</TableCell>
                    <TableCell className="px-4 py-3 text-sm text-slate-500">{p.owner_name || "—"}</TableCell>
                    <TableCell className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[p.status] || "bg-slate-800 text-slate-400 border-slate-700"}`}>{p.status}</span>
                    </TableCell>
                    <TableCell className="px-4 py-3 font-mono text-xs text-slate-500">{new Date(p.updated_at).toLocaleDateString("en-GB")}</TableCell>
                    <TableCell className="px-4 py-3">
                      <Link to={`/projects/${p.id}`} className="text-slate-500 hover:text-blue-400 transition-colors" data-testid={`review-project-${p.id}`}>
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow className="border-slate-800">
                  <TableCell colSpan={6} className="text-center py-12 text-sm text-slate-500">No projects awaiting approval</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
